/**
 * Third-Party Keyboard Accessibility Handler
 *
 * Comprehensive solution for Zinrelo loyalty app close buttons accessibility.
 * Addresses WCAG 2.1 A F54 - Section 508 (2017) A F54 compliance.
 */

// Element cache for performance optimization (declared early to avoid hoisting issues)
const elementCache = {
  closeBtn: null,
  closeBtnX: null,
  lastCacheUpdate: 0,
  cacheTimeout: 1000 // Update cache every 1 second
};

// Function to update element cache
function updateElementCache() {
  const now = Date.now();
  if (now - elementCache.lastCacheUpdate < elementCache.cacheTimeout) {
    return; // Cache is still fresh
  }

  elementCache.closeBtn = document.getElementById('zrl_ld_close_button');
  elementCache.closeBtnX = document.querySelector('div[onclick*="ss_mi.hide_product_preview"][role="button"]');
  elementCache.lastCacheUpdate = now;
}

// Function to check if element is a target button
function isTargetButton(element) {
  if (!element) return false;

  // Check if it's the main close button
  if (element.id === 'zrl_ld_close_button') return true;

  // Check if it's the X close button
  if (element.matches && element.matches('div[onclick*="ss_mi.hide_product_preview"][role="button"]')) return true;

  return false;
}

// Function to fix accessibility issues for target elements
function fixAccessibilityIssues() {
  // Target 1: Main close button - fix presentational children issue
  const closeBtn = document.getElementById('zrl_ld_close_button');

  if (closeBtn) {
    // Check for focusable child elements that could cause presentational children issue
    const focusableChildren = closeBtn.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

    if (focusableChildren.length > 0) {
      // Remove tabindex from child elements to prevent focus conflict
      focusableChildren.forEach(child => {
        if (child.hasAttribute('tabindex')) {
          child.removeAttribute('tabindex');
        }
        // Make child elements not focusable
        child.setAttribute('tabindex', '-1');
      });
    }
  }

  // Target 2: Close button with X symbol - fix accessibility issues
  const closeBtnX = document.querySelector('div[onclick*="ss_mi.hide_product_preview"][aria-hidden="true"]');

  if (closeBtnX) {
    // Remove aria-hidden so it's visible to assistive technology
    closeBtnX.removeAttribute('aria-hidden');

    // Add missing role="button"
    closeBtnX.setAttribute('role', 'button');

    // Add proper aria-label for accessibility
    closeBtnX.setAttribute('aria-label', 'Close Inner Circle dialog');

    // Ensure it's focusable
    if (!closeBtnX.hasAttribute('tabindex')) {
      closeBtnX.setAttribute('tabindex', '0');
    }

    // Check for focusable child elements in X button
    const focusableChildrenX = closeBtnX.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusableChildrenX.length > 0) {
      focusableChildrenX.forEach(child => {
        if (child.hasAttribute('tabindex')) {
          child.removeAttribute('tabindex');
        }
        child.setAttribute('tabindex', '-1');
      });
    }
  }
}

// Fix accessibility issues when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    fixAccessibilityIssues();
    updateElementCache(); // Initialize cache
  });
} else {
  fixAccessibilityIssues();
  updateElementCache(); // Initialize cache
}

// Also fix issues for dynamically added elements
let observer = null;
let isObserverActive = true;
let processedElements = new WeakSet(); // Track processed elements to avoid duplicate processing

function createObserver() {
  if (observer) return observer;

  observer = new MutationObserver((mutations) => {
    if (!isObserverActive) return;

    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && !processedElements.has(node)) {
            processedElements.add(node);

            // Update cache when new elements are added
            updateCacheOnNewElements(node);

            // Check for main close button
            const closeBtn = node.querySelector ?
              node.querySelector('#zrl_ld_close_button') :
              (node.matches && node.matches('#zrl_ld_close_button') ? node : null);

            if (closeBtn) {
              // Fix presentational children issue for main button
              const focusableChildren = closeBtn.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
              if (focusableChildren.length > 0) {
                focusableChildren.forEach(child => {
                  if (child.hasAttribute('tabindex')) {
                    child.removeAttribute('tabindex');
                  }
                  child.setAttribute('tabindex', '-1');
                });
              }
            }

            // Check for X close button
            const closeBtnX = node.querySelector ?
              node.querySelector('div[onclick*="ss_mi.hide_product_preview"][aria-hidden="true"]') :
              (node.matches && node.matches('div[onclick*="ss_mi.hide_product_preview"][aria-hidden="true"]') ? node : null);

            if (closeBtnX) {
              closeBtnX.removeAttribute('aria-hidden');
              closeBtnX.setAttribute('role', 'button');
              closeBtnX.setAttribute('aria-label', 'Close Inner Circle dialog');
              if (!closeBtnX.hasAttribute('tabindex')) {
                closeBtnX.setAttribute('tabindex', '0');
              }

              // Fix presentational children issue for X button
              const focusableChildrenX = closeBtnX.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
              if (focusableChildrenX.length > 0) {
                focusableChildrenX.forEach(child => {
                  if (child.hasAttribute('tabindex')) {
                    child.removeAttribute('tabindex');
                  }
                  child.setAttribute('tabindex', '-1');
                });
              }
            }
          }
        });
      }
    });
  });

  return observer;
}

// Start observer
createObserver().observe(document.body, { childList: true, subtree: true });

// Cleanup function to prevent memory leaks
function cleanup() {
  isObserverActive = false;
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  processedElements = new WeakSet();
}

// Disconnect observer when page is about to unload
window.addEventListener('beforeunload', cleanup);

// Disconnect observer after a reasonable time (5 minutes) to prevent indefinite running
setTimeout(() => {
  if (isObserverActive) {
    console.log('Third-party accessibility observer: Disconnecting after 5 minutes to prevent memory leaks');
    cleanup();
  }
}, 300000); // 5 minutes

// Expose cleanup function globally for manual cleanup if needed
window.__cleanupThirdPartyAccessibility = cleanup;

// Optimized keyboard event handler with event delegation
document.addEventListener('keydown', function (e) {
  // Only process Enter and Space keys
  if (e.key !== 'Enter' && e.key !== ' ') return;

  const focusedElement = document.activeElement;
  if (!focusedElement) return;

  // Check if focused element is a target button
  if (!isTargetButton(focusedElement)) {
    // Update cache periodically for new elements
    updateElementCache();
    return;
  }

  // Handle the keyboard event
  e.preventDefault(); // prevent page scroll or form submit
  focusedElement.click(); // triggers the same onclick handler
});

// Update cache when new elements are added via MutationObserver
function updateCacheOnNewElements(node) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    // Check if the added node or its children contain our target elements
    const hasMainBtn = node.id === 'zrl_ld_close_button' ||
                      (node.querySelector && node.querySelector('#zrl_ld_close_button'));
    const hasXBtn = (node.matches && node.matches('div[onclick*="ss_mi.hide_product_preview"][role="button"]')) ||
                    (node.querySelector && node.querySelector('div[onclick*="ss_mi.hide_product_preview"][role="button"]'));

    if (hasMainBtn || hasXBtn) {
      // Force cache update when target elements are added
      elementCache.lastCacheUpdate = 0;
      updateElementCache();
    }
  }
}
